import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ApiResponse } from '@/api/handlers/responses';
import { ProductQueryKeys } from './productQueryKeys';

/**
 * Deletes a product (`DELETE /products/{id}`) and, on success, invalidates every
 * list cache so the row drops out and **removes** the deleted product's detail
 * cache entry. Removing (rather than just invalidating) the detail query means a
 * later visit to the deleted product's detail/edit URL refetches and shows the
 * 404 state, instead of serving the stale cached product for the remaining
 * `staleTime` window. The caller navigates back to the list.
 */
export const useDeleteProductMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<void>, Error, string>({
        mutationFn: (id) => apiClient.products.deleteProduct(id),
        onSuccess: (result, id) => {
            if (result.success) {
                queryClient.removeQueries({
                    queryKey: ProductQueryKeys.detail(id),
                });
                queryClient.invalidateQueries({
                    queryKey: ProductQueryKeys.lists(),
                });
            }
        },
    });
};
